/** The backoffice API client (04-architecture § 8.1). */

import { createApiClient } from '@cowoo/shared';
import { env } from '../env';
import { useAdminStore } from './store';

export const api = createApiClient({
  baseUrl: env.API_BASE_URL,
  appInfo: { app: 'admin', version: env.APP_VERSION, platform: 'web' },
  getToken: () => useAdminStore.getState().token,
  onUnauthorized: () => useAdminStore.getState().clear(),
});

/**
 * Multipart uploads go around the typed client (10-web-admin § 6.2).
 *
 * `openapi-fetch` types a request body from the OpenAPI schema, and a
 * `multipart/form-data` body has no schema to type — the parts are read from
 * the stream. Forcing one through would mean lying to the type system about a
 * shape it cannot describe, so this is a plain `fetch` that carries the same
 * auth header and nothing else. The server still validates every byte.
 */
export async function postMultipart<T>(
  path: string,
  form: FormData,
): Promise<{ data: T | null; status: number }> {
  const token = useAdminStore.getState().token;
  // The same `/v1` the typed client prefixes every path with — without it the
  // upload went to a route that does not exist, and the 404 reached the
  // screen as "Bitte prüfe deine Eingaben".
  const response = await fetch(`${env.API_BASE_URL.replace(/\/$/, '')}/v1${path}`, {
    method: 'POST',
    // No `Content-Type`: the browser sets it with the multipart boundary.
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (response.status === 401) useAdminStore.getState().clear();
  const data = response.ok ? ((await response.json()) as T) : null;
  return { data, status: response.status };
}
